import { useState } from 'react';
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { CopyValueButton } from './CopyValueButton';

/** A fixed mask, deliberately unrelated to the real value.
 *
 * Sizing it to the plaintext would leak the one derived fact the API withholds
 * on purpose: how long the stored value is. The service sends no masked
 * fragment, no length and no hash for a secret (FR-010), and a mask that
 * tracked the value would put a length back on screen for variables while
 * making the two kinds visibly different lengths — reintroducing, in dots, the
 * distinction this column is trying to state once and calmly. */
const MASK = '••••••••••••';

/**
 * The value column: one shape for both kinds, one affordance apart.
 *
 * Both kinds render the same mask, so the column reads as a column. What
 * differs is what sits beside it — and that difference is the honest one:
 *
 * - A **variable** gets a working eye toggle. Its value is readable by
 *   definition, so revealing it costs nothing that the API has not already
 *   granted.
 * - A **secret** gets a lock, not a disabled eye. The service returns
 *   `value: null` for one and stores nothing derived from the plaintext, so
 *   there is nothing behind a reveal control. A greyed-out eye would still
 *   advertise a value that could be shown "if only" — and send someone hunting
 *   for the permission that would unlock it.
 *
 * Copy is offered while the value is still masked. Requiring a reveal in order
 * to copy would put the plaintext on screen for no reason other than ceremony.
 *
 * The text takes the free space and the controls sit at the cell's trailing
 * edge, so the toggle occupies the same x in every row and in both states.
 * Sizing the text to its content instead put the eye wherever the value
 * happened to end — and moved it out from under the pointer on the very click
 * that revealed the value, so a second click to re-hide it missed.
 */
export function SecretValueCell({
  entry,
  className,
}: {
  entry: WorkspaceSecretView;
  className?: string;
}) {
  const [revealed, setRevealed] = useState(false);

  // Readability follows the *kind*, never whether `value` happens to be null.
  // Deriving it from the data would render a variable whose value is genuinely
  // empty as a secret, and — worse — would let a kind added later inherit
  // "readable" from a null it happened to send.
  const readable = entry.kind === 'variable';

  return (
    <span className={cn('flex min-w-0 items-center gap-1', className)}>
      {revealed && readable ? (
        entry.value === '' ? (
          <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs italic">
            empty
          </span>
        ) : (
          <span className="text-muted-foreground min-w-0 flex-1 truncate font-mono text-xs">
            {entry.value}
          </span>
        )
      ) : (
        <span
          data-testid="value-mask"
          aria-hidden="true"
          className="text-muted-foreground min-w-0 flex-1 truncate font-mono text-xs tracking-[0.15em] select-none"
        >
          {MASK}
        </span>
      )}

      {readable ? (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={() => {
              setRevealed(!revealed);
            }}
          >
            {revealed ? (
              <EyeOffIcon aria-hidden="true" />
            ) : (
              <EyeIcon aria-hidden="true" />
            )}
            <span className="sr-only">
              {revealed
                ? `Hide the value of ${entry.key}`
                : `Show the value of ${entry.key}`}
            </span>
          </Button>
          <CopyValueButton value={entry.value ?? ''} label={entry.key} />
        </>
      ) : (
        // Not a button, and not focusable: there is no action here to reach.
        <span
          className="text-muted-foreground flex size-7 shrink-0 items-center justify-center"
          title="Write-only — this value can never be displayed"
        >
          <LockIcon aria-hidden="true" className="size-3.5" />
          <span className="sr-only">
            Write-only — this value can never be displayed
          </span>
        </span>
      )}
    </span>
  );
}
